// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file MobileNav.tsx
 * @input Uses React, useEffect, useRef, useCallback, ReactNode, StyleX
 * @output Exports MobileNav component and MobileNavProps
 * @position Core implementation; consumed by index.ts
 *
 * Full-height slide-out drawer overlay for mobile navigation.
 * The mobile counterpart to SideNav — accepts the same children
 * (SideNavSection, SideNavItem, or any ReactNode).
 *
 * Uses the native `<dialog>` element with `showModal()` for top-layer rendering.
 * This eliminates z-index stacking issues — the drawer renders above everything
 * without manual z-index management. The browser provides:
 * - Top layer promotion (no z-index needed)
 * - `::backdrop` pseudo-element
 * - Body scroll lock
 * - Focus trapping
 * - Escape key handling via `cancel` event
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/MobileNav/index.ts (exports if types change)
 * - /packages/cli/templates/blocks/components/MobileNav/ (showcase blocks)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, durationVars, easeVars, spacingVars } from "../theme/tokens.stylex.js";
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import { Heading } from "../Heading/Heading.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { mergeProps, mergeRefs, composeEventHandlers } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  dialog: {
    kVAEAm: "xixxii4",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    ks0D6T: "x1x1rfll",
    kskxy: "x7ab17h",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xn9wirt",
    kZKoxP: "xtdtrs8",
    kWkggS: "xjbqb8w",
    kVQacm: "xb3r6kr",
    kZeWKH: "xish69e",
    kFalU9: "x5ve5x3",
    kI3sdo: "x1a2a7pz",
    k1xSpc: "x1s85apg",
    $$css: true
  },
  open: {
    k1xSpc: "x78zum5",
    $$css: true
  },
  backdrop: {
    kGyWv1: "xnixb3f",
    kba3nw: "x1abwkk1",
    k5sjJv: "xph5o2a",
    kND0Po: "x167zut7",
    k9an0g: "xft5bk6",
    kb4ib: "x15h3t91",
    kA5Tbj: "x1viac0w",
    $$css: true
  },
  backdropOpen: {
    k5sjJv: "xb3n6bw",
    $$css: true
  },
  drawer: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x13vifvy",
    krVfgx: "x1ey2m1c",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kWkggS: "x10xzikg",
    kB7OPa: "x9f619",
    kVQacm: "xb3r6kr",
    k1ekBW: "x11xpdln",
    kIyJzY: "x80gvsz",
    kAMwcw: "xlr8y92",
    kI3sdo: "x1a2a7pz",
    k6CgDc: "xzg1mie",
    $$css: true
  },
  drawerStart: {
    kLqNvP: "x1o0tod",
    ke9TFa: "xw8tdv1",
    k8ry5P: "x18b5jzi",
    kBCPoo: "x1gejf6u",
    k3aq6I: "x5i6ehr xttggg",
    $$css: true
  },
  drawerStartOpen: {
    k3aq6I: "xbryuvx",
    $$css: true
  },
  drawerEnd: {
    kt4wiu: "xtijo5x",
    k2ei4v: "xgbv0en",
    kVhnKS: "x1t7ytsu",
    kGJrpR: "x1j92z86",
    k3aq6I: "xumwmo6 x1df3fe5",
    $$css: true
  },
  drawerEndOpen: {
    k3aq6I: "xbryuvx",
    $$css: true
  }
};
const _temp = {
  kzqmXN: "xn9wirt",
  ks0D6T: "xf68679",
  "$$css": true
};
const dynamicStyles = {
  width: w => [_temp, {
    "--x-maxWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${w}px`)
  }]
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * A slide-out drawer overlay for mobile navigation.
 *
 * The mobile counterpart to SideNav. Renders a full-height drawer that slides
 * in from the start (left in LTR) or end (right in LTR) edge of the viewport,
 * with a semi-transparent backdrop behind it.
 *
 * Uses the native `<dialog>` element with `showModal()` for top-layer rendering,
 * which provides built-in focus trapping, body scroll lock, and `::backdrop`.
 * No manual z-index needed — the browser's top layer handles stacking.
 *
 * When used inside AppShell, `isOpen` and `onOpenChange` are managed
 * automatically via context. When used standalone, provide them as props.
 *
 * @example
 * ```
 * <AppShell mobileNav={
 *   <MobileNav header="Navigation">
 *     <SideNavItem label="Home" href="/" />
 *   </MobileNav>
 * }>
 * <MobileNav isOpen={isOpen} onOpenChange={setIsOpen} header="Navigation">
 *   <SideNavItem label="Home" href="/" />
 * </MobileNav>
 * ```
 */
export function MobileNav({
  isOpen: isOpenProp,
  onOpenChange: onOpenChangeProp,
  children,
  header,
  width = 320,
  side = 'auto',
  label,
  'data-testid': testId,
  xstyle,
  className,
  style,
  onClick: onClickProp,
  ref,
  ...rest
}) {
  const t = useTranslator();
  // Read from AppShell context as fallback
  const appShellMobile = useAppShellMobile();
  const isOpen = isOpenProp ?? appShellMobile.isMobileNavOpen;
  const onOpenChange = useMemo(() => onOpenChangeProp ?? (open => {
    if (open) {
      appShellMobile.openMobileNav();
    } else {
      appShellMobile.closeMobileNav();
    }
  }), [onOpenChangeProp, appShellMobile]);
  const dialogRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  // Resolved side — computed from trigger position when side='auto'
  const [resolvedSide, setResolvedSide] = useState(side === 'auto' ? 'end' : side);

  // Open/close the dialog via showModal()/close()
  // close() is delayed so the slide-out transition can play.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (isOpen) {
      // Determine drawer side from trigger position when auto
      if (side === 'auto') {
        const trigger = document.activeElement;
        if (trigger && trigger !== document.body) {
          const rect = trigger.getBoundingClientRect();
          const triggerCenter = rect.left + rect.width / 2;
          // eslint-disable-next-line @eslint-react/set-state-in-effect -- side is resolved from trigger layout immediately before showModal()
          setResolvedSide(triggerCenter < window.innerWidth / 2 ? 'start' : 'end');
        }
      } else {
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- side prop changes must update the open dialog placement
        setResolvedSide(side);
      }
      if (!dialog.open) {
        dialog.showModal();
      }
      // Prevent background scrolling and iOS pull-to-refresh.
      // overflow: clip avoids creating a scroll container (unlike hidden),
      // so there's no scroll bounce and no need to save/restore scroll position.
      document.documentElement.style.overflow = 'clip';
    } else if (dialog.open) {
      document.documentElement.style.overflow = '';
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 10 : 250;
      closeTimeoutRef.current = setTimeout(() => {
        dialog.close();
      }, duration);
    }
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      document.documentElement.style.overflow = '';
      // Close the native dialog on teardown if it's still open. Inside AppShell
      // the drawer is mounted in an <Activity> that switches to mode="hidden"
      // when the drawer closes; React then runs this cleanup (with a stale
      // isOpen) instead of re-running the effect with isOpen=false, so the
      // close branch above never fires. If we leave the <dialog> `open` here,
      // showModal() is skipped on the next open (the dialog is already open in
      // the hidden tree) and the drawer can never be re-opened. Closing it
      // unconditionally on teardown keeps the native dialog state in sync so a
      // subsequent open cleanly calls showModal() again.
      if (dialog.open) {
        dialog.close();
      }
    };
  }, [isOpen, side]);

  // Handle native cancel event (Escape key) — prevent default and route through onOpenChange
  const handleCancel = useCallback(event => {
    event.preventDefault();
    onOpenChange(false);
  }, [onOpenChange]);

  // Handle clicks on the dialog backdrop area (outside the drawer)
  const handleDialogClick = useCallback(event => {
    // Only close if click was directly on the dialog element (the transparent overlay),
    // not on the drawer or its children
    if (event.target === event.currentTarget) {
      onOpenChange(false);
    }
  }, [onOpenChange]);
  const isStart = resolvedSide === 'start';
  return /*#__PURE__*/_jsx("dialog", {
    ref: mergeRefs(ref, dialogRef),
    ...mergeProps(themeProps('mobile-nav', {
      side: resolvedSide
    }), stylex.props(styles.dialog, isOpen && styles.open, styles.backdrop, isOpen && styles.backdropOpen, xstyle), className, style),
    ...rest,
    "data-testid": testId,
    "aria-label": label ?? (typeof header === 'string' ? header : t('@astryx.mobileNav.navigation')),
    onClick: composeEventHandlers(onClickProp, handleDialogClick),
    onCancel: handleCancel,
    children: /*#__PURE__*/_jsxs("div", {
      tabIndex: -1,
      ...stylex.props(styles.drawer, dynamicStyles.width(width), isStart && styles.drawerStart, isStart && isOpen && styles.drawerStartOpen, !isStart && styles.drawerEnd, !isStart && isOpen && styles.drawerEndOpen),
      children: [/*#__PURE__*/_jsxs("div", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 x1qughib x1k15mir xf314gf x2lah0s x92x3c3 x1q0q8m5 xw8gpjh"
          },
          1: {
            className: "x78zum5 x6s0dn4 x1k15mir xf314gf x2lah0s x92x3c3 x1q0q8m5 xw8gpjh x13a6bvl"
          }
        }[!!!header << 0],
        children: [typeof header === 'string' ? /*#__PURE__*/_jsx("span", {
          ...{
            className: "x11g1kdw"
          },
          children: /*#__PURE__*/_jsx(Heading, {
            level: 2,
            children: header
          })
        }) : header ?? null, /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          label: t('@astryx.mobileNav.closeNavigation'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            color: "inherit"
          }),
          onClick: () => onOpenChange(false),
          isIconOnly: true
        })]
      }), /*#__PURE__*/_jsx("div", {
        ...{
          className: "x98rzlu x1odjw0f x6ikm8r xish69e xx69xxh xf314gf xce4md1"
        },
        children: children
      })]
    })
  });
}
MobileNav.displayName = 'MobileNav';